import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

export interface SearchableOption {
  /** The value stored on the form — never the display label. */
  value: string;
  label: string;
  /** Options of one group, kept next to each other, are listed under this heading. */
  group?: string;
}

/**
 * Single-select with type-to-filter — a drop-in replacement for a long `<select>`.
 *
 * Built for the lists that are painful to scroll (every Indian state; the hundreds
 * of cities in one state). Search matches the LABEL, so the decorations those lists
 * carry — "Haryana — North", "Ambala — Tier 1" — are searchable too: typing "north"
 * or "tier 1" narrows the list, not just the name.
 *
 * The panel is a FIXED-position portal anchored to the control, repositioned on
 * scroll (capture: true, so a modal's own scrolling counts) and on resize, and it
 * flips above the control when there isn't room below. That is what keeps it from
 * being clipped inside a scrolling dialog — the same approach the dashboard's
 * filter modal uses for its multi-selects.
 *
 * ⚠️ The panel portals to <body>, so it is a SIBLING of every dialog — not a child.
 * Its z-index must therefore beat the dialog hosting the control, not just sit
 * above the page. The app's ladder today: 60 dashboard filter modal · 100 the
 * shared `Modal` (Add/Edit Store) · 110 second-level modals · 120 tooltips ·
 * 130 the remarks modal. 140 clears all of them. At the original 80 the list
 * rendered BEHIND the z-[100] `Modal` and looked like an empty dropdown even
 * though the options had loaded. Only one panel is open at a time and it closes
 * on outside-click/Escape, so sitting on top is safe.
 */
export default function SearchableSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  disabled = false,
  allowClear = true,
  emptyText = 'No matches',
  triggerClassName,
  minPanelWidth = 0,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Offer a row that resets the field to empty (mirrors a <select>'s blank option). */
  allowClear?: boolean;
  emptyText?: string;
  /** Draws the control in another shape (a small chip, say) in place of the field's. */
  triggerClassName?: string;
  /** The list may be wider than a small control. */
  minPanelWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? options.filter((o) => o.label.toLowerCase().includes(t)) : options;
  }, [options, q]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  );

  // A fresh search each time it opens, with the highlight back at the top.
  useEffect(() => {
    if (!open) { setQ(''); setActive(0); return; }
  }, [open]);
  useEffect(() => { setActive(0); }, [q]);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const H = 320; // approx panel height, incl. the search box
      const below = window.innerHeight - r.bottom;
      const openUp = below < H && r.top > below;
      setPos({
        left: r.left,
        width: Math.max(r.width, minPanelWidth),
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, minPanelWidth]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Keep the highlighted row visible while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    // By role, not by position: headings and the clear row sit among the options.
    const el = listRef.current?.querySelectorAll('[role="option"]')[active] as HTMLElement | undefined;
    // Guarded: jsdom has no scrollIntoView, and neither do some older browsers —
    // an unguarded call throws and takes the whole control down.
    if (typeof el?.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter takes the highlighted row — with one match left, that is simply it.
      const hit = shown[active];
      if (hit) pick(hit.value);
    }
  };

  return (
    <>
      <button
        id={id}
        ref={btnRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={triggerClassName ?? `flex w-full items-center justify-between rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 ${
          disabled ? 'cursor-not-allowed bg-gray-50 text-gray-400' : 'bg-white'
        }`}
      >
        <span className={`truncate text-left ${selectedLabel ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 140 }}
          className="rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <div className="border-b border-gray-100 p-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={searchPlaceholder}
              autoFocus
              className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div ref={listRef} role="listbox" className="max-h-60 overflow-y-auto py-1">
            {allowClear && value !== '' && q.trim() === '' && (
              <div
                onClick={() => pick('')}
                className="cursor-pointer px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
              >
                {placeholder}
              </div>
            )}
            {shown.map((o, i) => {
              const on = o.value === value;
              return (
                <Fragment key={o.value}>
                  {o.group && o.group !== shown[i - 1]?.group && (
                    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {o.group}
                    </div>
                  )}
                  <div
                    role="option"
                    aria-selected={on}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(o.value)}
                    className={`flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm ${
                      on ? 'bg-primary-50 text-primary-700' : i === active ? 'bg-gray-50 text-gray-700' : 'text-gray-700'
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {on && <Check className="h-4 w-4 flex-shrink-0 text-primary-600" />}
                  </div>
                </Fragment>
              );
            })}
            {shown.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">{emptyText}</div>}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
